import { Box, TableContainer } from '@material-ui/core';
import { downloadReport, FilterOperator, formatDate } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { SliderWarning } from '@/components/modules/Cart/components/OrderLines/components/OrderTable/components/SliderWarning';
import { Body } from '@/components/modules/Reports/ReportDetails/ReportPayoutsTable/components/Body';
import { ReportsListRowProps } from '@/components/modules/Reports/ReportsList/types/ReportsListRowProps';
import { CustomHeader } from '@/components/shared/components/CustomTable/CustomHeader';
import { Font } from '@/components/shared/components/Font';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useDefaultTable } from '@/components/shared/hooks/useDefaultTable';
import { TableColumn } from '@/config/types';
import { useScreen } from '@/hooks/useScreen';
import { downloadFileFromBlob } from '@/utils/downloadFileFromBlob';
import { queryStringBuilder } from '@/utils/queryStringBuilder';

export interface ReportPayoutsTableProps {
  report: ReportsListRowProps['row'];
}

export const ReportPayoutsTable = ({ report }: ReportPayoutsTableProps) => {
  const tableElementId = 'report-payout-table-container';
  const { classes, stickyCellStyle, isScrollable } = useDefaultTable({
    tableElementId,
  });
  const { isMobile } = useScreen();
  const [loading, setLoading] = useState(false);

  const columns = getReportPayoutsTableColumns({ isMobile });
  report.payouts[0].disbursedAmount;

  const downloadFile = async () => {
    setLoading(true);
    try {
      const data = await downloadReport({
        query: queryStringBuilder({
          page: 1,
          pageSize: 10,
          orderBy: 'report_date',
          direction: 'desc',
          filters: [{ operator: FilterOperator.EQUAL, field: 'id', value: report.id }],
          search: '',
        }),
      });
      downloadFileFromBlob({
        data,
        fileName: `${report.businessName}_${formatDate(report.reportDate)}`,
        fileExtension: 'csv',
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box position={'relative'} width={isMobile ? '100%' : 'fit-content'}>
      <Font variant="h4" color="textPrimary" style={{ marginBottom: '24px' }}>
        <Text tId={'PAYOUTS'} />
      </Font>
      <StandardButton
        loading={loading}
        size="small"
        variant="outlined"
        onClick={downloadFile}
        style={{ position: 'absolute', top: 2, right: 0 }}
      >
        <Text tId={'DOWNLOAD'} />
      </StandardButton>
      <TableContainer id={tableElementId}>
        <CustomHeader tableProps={{ classes, stickyCellStyle }} columns={columns} />
        <Body report={report} tableProps={{ classes, stickyCellStyle }} />
      </TableContainer>
      {isScrollable && (
        <>
          <SliderWarning />
        </>
      )}
    </Box>
  );
};

export const getReportPayoutsTableColumns = ({ isMobile }: { isMobile: boolean; deleteView?: boolean }) => {
  const columnsDesktop: TableColumn[] = [
    { name: 'lender', label: 'ENTITY', align: 'center', colWidth: '80px', sticky: true },
    { name: 'payout_date', label: isMobile ? 'DATE' : 'PAYOUT_DATE', align: 'right' },
    { name: 'reference', label: 'REFERENCE', align: 'left', colWidth: '140px' },
    { name: 'batch_number', label: isMobile ? 'BATCH' : 'BATCH_NUMBER', align: 'right', colWidth: '130px' },
    {
      name: 'adjustments_amount',
      label: isMobile ? 'ADJUSTMENTS' : 'ADJUSTMENTS_AMOUNT',
      align: 'right',
      colWidth: '130px',
    },
    { name: 'disbursed_amount', label: isMobile ? 'DISBURSED' : 'DISBURSED_AMOUNT', align: 'right', colWidth: '130px' },
    { name: 'go_to_payout', label: '', align: 'right', colWidth: '36px' },
  ];

  const columnsMobile: TableColumn[] = columnsDesktop;

  return isMobile ? columnsMobile : columnsDesktop;
};
